import { FallbackRender } from '@sentry/react';
import { Button } from 'base/ui/button';
import { IconRefresh } from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import { Link } from 'base/ui/link';
import { DividerLine } from 'base/ui/panel';
import {
  TextRegular,
  TextSmall,
  TextSmallLight,
  TextTinyLight,
  Title1,
} from 'base/ui/typography';
import React, { useEffect } from 'react';
import { getUserJwt } from '../../library/src/modules/cookies';
import {
  AuthRoute,
  authRouteToUrl,
  getCurrentLocationForRedirect,
} from './auth/routes';
import { dashboardRouteToUrl } from './dashboard/routes';
import { ProgressBar } from './editor/loading_curtain';
import styles from './login_wall.css';

export function LoginWall({
  children,
  path = 'login',
  context,
}: {
  children: React.ReactNode;
  path?: 'login' | 'signup';
  context?: AuthRoute['context'];
}) {
  const token = getUserJwt();
  if (token) return <>{children}</>;
  return (
    <RedirectOnLoad
      name="login"
      url={authRouteToUrl({
        path,
        context: { redirect: getCurrentLocationForRedirect(), ...context },
      })}
    />
  );
}

export function RedirectOnLoad({
  name,
  url,
  replace,
}: {
  name: string;
  url: string;
  replace?: boolean;
}) {
  useEffect(() => {
    if (replace) {
      window.history.replaceState({}, '', url);
    } else {
      window.location.href = url;
    }
  }, [replace, url]);
  return (
    <div className={styles.middle}>
      <Rows spacing={8}>
        <TextSmall color="grey">Redirecting to {name}...</TextSmall>
        <TextSmallLight color="grey">
          If you are not redirected,{' '}
          <Link href={url} target="_self">
            click here
          </Link>
          .
        </TextSmallLight>
      </Rows>
    </div>
  );
}

export const renderErrorFallback: FallbackRender = ({
  error,
  componentStack,
}) => {
  return (
    <div className={styles.middle}>
      <Rows spacing={16} xalign="center">
        <TextSmall color="grey">Oh no, the app just crashed!</TextSmall>
        <Button
          label="Reload"
          icon={IconRefresh}
          variant="primary"
          onClick={() => window.location.reload()}
        />
        <TextSmall color="grey">
          If this keeps happening, please{' '}
          <Link href="https://www.relume.io/troubleshooting">contact us</Link>.
        </TextSmall>

        <DividerLine />
        <TextTinyLight color="grey" align="left" selectable>
          Technical details:
          <pre>
            {`${error}`}
            {componentStack}
          </pre>
        </TextTinyLight>
      </Rows>
    </div>
  );
};

export const LoadingPage = () => (
  <div className={styles.middle}>
    <ProgressBar />
  </div>
);

export function NotFoundPage() {
  return (
    <div className={styles.middle}>
      <Rows spacing={8} xalign="center">
        <Title1>404</Title1>
        <TextSmallLight color="grey">
          The page you are looking for was not found
        </TextSmallLight>
        <div />
        <Button
          label="Go to dashboard"
          variant="primary"
          href={dashboardRouteToUrl({ path: 'dashboard' })}
        />
      </Rows>
    </div>
  );
}

export function DebugHomePage() {
  return (
    <div className={styles.middle}>
      <Rows spacing={16} xalign="center">
        <Title1>Debug Home Page</Title1>
        <TextRegular color="grey">
          In production & staging, this handled by webflow instead.
        </TextRegular>
        <div />
        <Inline spacing={16}>
          <Button
            href="/app/login"
            label="Login page"
            size="large"
            variant="primary"
          />
          <Button
            href="/dashboard"
            label="Dashboard (logged in)"
            size="large"
          />
        </Inline>
      </Rows>
    </div>
  );
}
